import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { ReachButton } from '../ReachButton/ReachButton';
import { AccentedText } from '../AccentedText/AccentedText';
import './HomepageHero.scss';
import { processLink } from '../../shared/processLink';

export interface HomepageHeroProps {
    heading: string;
    subheading?: string;
    heroImage?: IGatsbyImageData;
    heroImageAlt?: string;
    callsToAction?: {
        title: string;
        uri: string;
    }[];
}

const useWindowSize = () => {
    const [size, setSize] = useState([0, 0]);
    useEffect(() => {
        function updateSize() {
            setSize([window.innerWidth, window.innerHeight]);
        }
        window.addEventListener(`resize`, updateSize);
        updateSize();
        return () => window.removeEventListener(`resize`, updateSize);
    }, []);
    return size;
};

export const HomepageHero = (props: HomepageHeroProps) => {
    const [, windowHeight] = useWindowSize();
    const [className, setClassName] = useState(``);

    /**
     * Fix bug in Safari. After hydration, the dark image overlay will sometimes
     * "double render" on Safari if the page loads in a specific manner. This
     * causes the hero image to be darker than intended.
     * This is clearly a Safari bug as the computed CSS properties are correct and
     * merely resizing the window by 1px fixes the issue when a redraw occurs.
     */
    useEffect(() => {
        const w = window as any;
        const isSafari =
            /constructor/i.test(w[`HTMLElement`]) ||
            (function (p) {
                return p.toString() === `[object SafariRemoteNotification]`;
            })(
                !w[`safari`] ||
                    (typeof w[`safari`] !== `undefined` &&
                        w[`safari`].pushNotification)
            );

        if (isSafari) {
            setClassName(`no-bg`);
        }
    }, []);

    return (
        <section
            className={`themed-container homepage-hero dark 
                ${props.callsToAction ? `adapt` : ``} 
                ${className}`}
            style={{
                height: windowHeight ? windowHeight : `100vh`,
            }}
        >
            <div className="kite-container">
                <h1 className="typestack-display">
                    <AccentedText>{props.heading}</AccentedText>
                </h1>
                {props.subheading && <p>{props.subheading}</p>}
                {props.callsToAction?.length > 0 && (
                    <nav className="buttons" aria-label="Calls to action">
                        <ul>
                            {props.callsToAction
                                ?.filter((link) => link)
                                .map((link, idx) => (
                                    <li key={idx}>
                                        <ReachButton
                                            buttonStyle={
                                                idx === 0
                                                    ? `primary`
                                                    : `secondary`
                                            }
                                            href={processLink(link.uri)}
                                        >
                                            {link.title}
                                        </ReachButton>
                                    </li>
                                ))}
                        </ul>
                    </nav>
                )}
                <GatsbyImage
                    image={props.heroImage}
                    alt=""
                    role="presentation"
                />
            </div>
        </section>
    );
};
